import { BpmnModdle, type ModdleElement } from 'bpmn-moddle';
import { contentDigest } from '../lib/digest';
import {
  DECISION_TABLE_REFERENCE_KEY,
  FlowableAttr,
  FlowableEl,
  FlowableTaskType,
  flowableAttrName,
  flowableModdleDescriptor,
  flowableTypeName,
} from './flowable';
import type {
  AggregationVariable,
  BpmnDefinitionsModel,
  BpmnElement,
  BpmnFlow,
  BpmnLane,
  BpmnProcessModel,
  BpmnRelationship,
  BpmnStartEvent,
  BpmnTextAnnotation,
  BpmnWarning,
  FlowableTaskKind,
  MultiInstance,
  RelationshipType,
  TimerDefinition,
  WorkerParam,
} from './types';

/**
 * bpmn:Relationshipのsource/targetはBPMN標準moddleでは参照(isReference)で、
 * 解決できないIDはmoddleが黙って落としてしまい参照切れを報告できない。
 * 出典索引にはID文字列そのものが要るので、Stringの繰り返しへ再定義して読む。
 * 表示専用の読み替えであり、XMLへは書き戻さない（uri/prefixはmoddleのパッケージ鍵としてだけ使う）。
 */
const relationshipIdDescriptor = {
  name: 'RelationshipIds',
  uri: 'urn:internal-poc4:bpmn-relationship-ids',
  prefix: 'relids',
  types: [
    {
      name: 'RelationshipIdRefs',
      isAbstract: true,
      extends: ['bpmn:Relationship'],
      properties: [
        { name: 'source', type: 'String', isMany: true, redefines: 'bpmn:Relationship#source' },
        { name: 'target', type: 'String', isMany: true, redefines: 'bpmn:Relationship#target' },
      ],
    },
  ],
};

/** docs/microapp-package.mdが初期runtimeで許可するflowNodeの型。sequenceFlowは別扱い。 */
const ALLOWED_NODE_TYPES = new Set([
  'bpmn:StartEvent',
  'bpmn:EndEvent',
  'bpmn:IntermediateCatchEvent',
  'bpmn:ExclusiveGateway',
  'bpmn:ParallelGateway',
  'bpmn:BoundaryEvent',
  'bpmn:ServiceTask',
  'bpmn:UserTask',
  'bpmn:CallActivity',
]);

const ALLOWED_START_EVENT_DEFINITIONS = new Set([
  'bpmn:MessageEventDefinition',
  'bpmn:TimerEventDefinition',
]);

/**
 * 開始イベント以外で許可する定義。中間キャッチと境界はタイマーだけ（messageの入口が無い）、
 * 終了はterminateだけ。開始イベントは上の集合で別に見る。
 */
const ALLOWED_EVENT_DEFINITIONS: Readonly<Record<string, ReadonlySet<string>>> = {
  'bpmn:IntermediateCatchEvent': new Set(['bpmn:TimerEventDefinition']),
  'bpmn:BoundaryEvent': new Set(['bpmn:TimerEventDefinition']),
  'bpmn:EndEvent': new Set(['bpmn:TerminateEventDefinition']),
};

const TIMER_KINDS: readonly TimerDefinition['kind'][] = ['timeDuration', 'timeDate', 'timeCycle'];

/** moddleが参照解決に失敗したときの警告。未対応拡張ではないので警告の引き取り対象から外す。 */
const UNRESOLVED_REFERENCE = 'unresolved reference';

/** moddleは重複IDの要素を木へ入れずメッセージだけで知らせるため、そこからIDを取り出す。 */
const DUPLICATE_ID = /duplicate ID <([^>]+)>/;

const isModdleElement = (value: unknown): value is ModdleElement =>
  typeof value === 'object' && value !== null && '$type' in value;

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const elements = (value: unknown): ModdleElement[] =>
  Array.isArray(value) ? value.filter(isModdleElement) : [];

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const idOf = (element: ModdleElement): string => text(element.get('id'));

/** 参照プロパティ（sourceRef等）は解決済み要素で入る。解決できなかった参照は空文字。 */
const refId = (value: unknown): string => (isModdleElement(value) ? idOf(value) : '');

const nameOf = (element: ModdleElement): string => text(element.get('name'));

const documentationOf = (element: ModdleElement): string =>
  elements(element.get('documentation'))
    .map((entry) => text(entry.get('text')))
    .join('\n')
    .trim();

const extensionValues = (element: ModdleElement): ModdleElement[] => {
  const extension = element.get('extensionElements');
  return isModdleElement(extension) ? elements(extension.get('values')) : [];
};

const eventDefinitionsOf = (element: ModdleElement): ModdleElement[] =>
  elements(element.get('eventDefinitions'));

const workerParams = (values: ModdleElement[], typeName: string): WorkerParam[] =>
  values
    .filter((value) => value.$type === typeName)
    .map((value) => ({
      source: str(value.get('source')),
      sourceExpression: str(value.get('sourceExpression')),
      target: str(value.get('target')),
      targetExpression: str(value.get('targetExpression')),
    }));

/** flowable:field name="decisionTableReferenceKey" の値。属性形式と子要素形式の両方を読む。 */
const decisionKeyOf = (values: ModdleElement[]): string | null => {
  const field = values.find(
    (value) =>
      value.$type === flowableTypeName(FlowableEl.field) &&
      value.get('name') === DECISION_TABLE_REFERENCE_KEY,
  );
  if (!field) return null;
  return str(field.get('stringValue')) ?? str(field.get('string')) ?? str(field.get('expression'));
};

const taskKindOf = (element: ModdleElement): FlowableTaskKind | null => {
  const value = element.get(flowableAttrName(FlowableAttr.type));
  if (value === FlowableTaskType.externalWorker) return 'external-worker';
  if (value === FlowableTaskType.dmn) return 'dmn';
  return null;
};

const multiInstanceOf = (element: ModdleElement): MultiInstance | null => {
  const loop = element.get('loopCharacteristics');
  if (!isModdleElement(loop) || loop.$type !== 'bpmn:MultiInstanceLoopCharacteristics') return null;
  // 集約定義はmultiInstance自身のextensionElementsにある（タスク側のfield等と混ぜない）。
  const aggregation = extensionValues(loop).find(
    (value) => value.$type === flowableTypeName(FlowableEl.variableAggregation),
  );
  const variables: AggregationVariable[] = aggregation
    ? elements(aggregation.get(`bpmn:${FlowableEl.variable}`)).map((variable) => ({
        source: str(variable.get('source')),
        sourceExpression: str(variable.get('sourceExpression')),
        target: text(variable.get('target')),
      }))
    : [];
  return {
    isSequential: loop.get('isSequential') === true,
    collection: str(loop.get(flowableAttrName(FlowableAttr.collection))),
    elementVariable: str(loop.get(flowableAttrName(FlowableAttr.elementVariable))),
    aggregationTarget: aggregation ? str(aggregation.get('target')) : null,
    aggregationVariables: variables,
  };
};

const timerOf = (element: ModdleElement): TimerDefinition | null => {
  const definition = eventDefinitionsOf(element).find(
    (candidate) => candidate.$type === 'bpmn:TimerEventDefinition',
  );
  if (!definition) return null;
  for (const kind of TIMER_KINDS) {
    const expression = definition.get(kind);
    if (isModdleElement(expression)) return { kind, value: text(expression.get('body')) };
  }
  return null;
};

const messageNameOf = (element: ModdleElement): string | null => {
  const definition = eventDefinitionsOf(element).find(
    (candidate) => candidate.$type === 'bpmn:MessageEventDefinition',
  );
  const message = definition?.get('messageRef');
  return isModdleElement(message) ? str(message.get('name')) : null;
};

const toElement = (node: ModdleElement): BpmnElement => {
  const values = extensionValues(node);
  return {
    id: idOf(node),
    type: node.$type,
    name: nameOf(node),
    documentation: documentationOf(node),
    flowableType: taskKindOf(node),
    topic: str(node.get(flowableAttrName(FlowableAttr.topic))),
    decisionKey: decisionKeyOf(values),
    calledElement: node.$type === 'bpmn:CallActivity' ? str(node.get('calledElement')) : null,
    workerIn: workerParams(values, flowableTypeName(FlowableEl.externalWorkerInParameter)),
    workerOut: workerParams(values, flowableTypeName(FlowableEl.externalWorkerOutParameter)),
    multiInstance: multiInstanceOf(node),
    boundary:
      node.$type === 'bpmn:BoundaryEvent'
        ? {
            attachedToRef: refId(node.get('attachedToRef')),
            // BPMN仕様の既定は中断（true）。省略時はmoddleが既定値を入れる。
            cancelActivity: node.get('cancelActivity') !== false,
          }
        : null,
    timer: timerOf(node),
    messageName: messageNameOf(node),
    terminate: eventDefinitionsOf(node).some(
      (definition) => definition.$type === 'bpmn:TerminateEventDefinition',
    ),
  };
};

const toFlow = (node: ModdleElement): BpmnFlow => {
  const condition = node.get('conditionExpression');
  return {
    id: idOf(node),
    name: nameOf(node),
    sourceRef: refId(node.get('sourceRef')),
    targetRef: refId(node.get('targetRef')),
    condition: isModdleElement(condition) ? str(condition.get('body')) : null,
  };
};

const toLane = (node: ModdleElement): BpmnLane => ({
  id: idOf(node),
  name: nameOf(node),
  flowNodeRefs: elements(node.get('flowNodeRef')).map(idOf),
});

const toStartEvent = (node: ModdleElement): BpmnStartEvent => {
  const definitions = eventDefinitionsOf(node);
  const messageName = messageNameOf(node);
  const timer = timerOf(node);
  return {
    id: idOf(node),
    name: nameOf(node),
    kind: definitions.some((d) => d.$type === 'bpmn:MessageEventDefinition')
      ? 'message'
      : timer !== null
        ? 'timer'
        : 'none',
    messageName,
    timer,
  };
};

/**
 * 登録原本のBPMN XMLを表示モデルへ解析する。
 * 壊れた定義でも表示を止めないため、検出した問題はwarningsへ積んで解析を続ける。
 */
export async function parseBpmnDefinitions(xml: string): Promise<BpmnDefinitionsModel> {
  const moddle = new BpmnModdle({
    flowable: flowableModdleDescriptor,
    relids: relationshipIdDescriptor,
  });
  const parsed = await moddle.fromXML(xml);
  const definitions = parsed.rootElement;
  const warnings: BpmnWarning[] = [];

  // moddleは未知の拡張要素（flowable:executionListener等）や重複IDの要素を木から落とすため、
  // moddle自身の警告を引き取らないと、それらが在ったこと自体を利用者へ知らせられない。
  for (const warning of parsed.warnings) {
    if (warning.message.startsWith(UNRESOLVED_REFERENCE)) continue;
    const duplicated = DUPLICATE_ID.exec(warning.message)?.[1];
    if (duplicated !== undefined) {
      warnings.push({
        code: 'duplicate_element_id',
        message: `ID ${duplicated} が重複しているため2つ目以降の要素を表示できません`,
        ref: duplicated,
      });
      continue;
    }
    warnings.push({
      code: 'unsupported_extension',
      message: warning.message,
      ref: warning.element ? idOf(warning.element) : null,
    });
  }

  const processes: BpmnProcessModel[] = [];

  for (const process of elements(definitions.get('rootElements'))) {
    if (process.$type !== 'bpmn:Process') continue;
    const processKey = idOf(process);

    const nodes: Record<string, BpmnElement> = {};
    const flows: BpmnFlow[] = [];

    // subProcessの中も表示対象にする（許可構文外でもXMLにある要素は隠さない）。
    const visit = (container: ModdleElement): void => {
      for (const node of elements(container.get('flowElements'))) {
        if (node.$type === 'bpmn:SequenceFlow') {
          const flow = toFlow(node);
          flows.push(flow);
          continue;
        }
        if (!ALLOWED_NODE_TYPES.has(node.$type)) {
          warnings.push({
            code: 'unsupported_element',
            message: `${node.$type} は初期runtimeの許可構文外です`,
            ref: idOf(node),
          });
        }
        const allowedDefinitions = ALLOWED_EVENT_DEFINITIONS[node.$type];
        const unsupportedDefinition = allowedDefinitions
          ? eventDefinitionsOf(node).find((definition) => !allowedDefinitions.has(definition.$type))
          : undefined;
        if (unsupportedDefinition) {
          warnings.push({
            code: 'unsupported_element',
            message: `${node.$type} の ${unsupportedDefinition.$type} は初期runtimeの許可構文外です`,
            ref: idOf(node),
          });
        }
        const element = toElement(node);
        nodes[element.id] = element;
        if (element.flowableType === null) {
          const rawType = str(node.get(flowableAttrName(FlowableAttr.type)));
          if (rawType !== null) {
            warnings.push({
              code: 'unsupported_extension',
              message: `flowable:type="${rawType}" は初期runtimeの許可構文外です`,
              ref: element.id,
            });
          }
        }
        visit(node);
      }
    };
    visit(process);

    const lanes: Record<string, BpmnLane> = {};
    for (const laneSet of elements(process.get('laneSets'))) {
      for (const lane of elements(laneSet.get('lanes'))) {
        const parsedLane = toLane(lane);
        lanes[parsedLane.id] = parsedLane;
      }
    }

    // プロセスの開始点はprocess直下のstartEventだけ。subProcess内の開始はプロセスの入口ではない。
    const startEvents: BpmnStartEvent[] = [];
    for (const node of elements(process.get('flowElements'))) {
      if (node.$type !== 'bpmn:StartEvent') continue;
      startEvents.push(toStartEvent(node));
      const unsupported = eventDefinitionsOf(node).find(
        (definition) => !ALLOWED_START_EVENT_DEFINITIONS.has(definition.$type),
      );
      if (unsupported) {
        warnings.push({
          code: 'unsupported_element',
          message: `開始イベントの ${unsupported.$type} は初期runtimeの許可構文外です`,
          ref: idOf(node),
        });
      }
    }

    // 出典カタログはprocess直下のtextAnnotationだけ。subProcess内の注記はカタログではない。
    const textAnnotations: Record<string, BpmnTextAnnotation> = {};
    for (const artifact of elements(process.get('artifacts'))) {
      if (artifact.$type !== 'bpmn:TextAnnotation') continue;
      const annotation: BpmnTextAnnotation = {
        id: idOf(artifact),
        text: text(artifact.get('text')),
      };
      if (annotation.text === '') {
        warnings.push({
          code: 'empty_annotation_text',
          message: `出典 ${annotation.id} の本文が空です`,
          ref: annotation.id,
        });
      }
      textAnnotations[annotation.id] = annotation;
    }

    processes.push({
      processKey,
      name: nameOf(process),
      documentation: documentationOf(process),
      isExecutable: process.get('isExecutable') === true,
      elements: nodes,
      lanes,
      flows,
      startEvents,
      textAnnotations,
    });
  }

  // 出典を付けられる対象（process/lane/sequenceFlow/flowNode）と、出典そのもの。
  const provenanceSources = new Set<string>();
  const provenanceTargets = new Set<string>();
  for (const process of processes) {
    provenanceSources.add(process.processKey);
    for (const id of Object.keys(process.elements)) provenanceSources.add(id);
    for (const id of Object.keys(process.lanes)) provenanceSources.add(id);
    for (const flow of process.flows) provenanceSources.add(flow.id);
    for (const id of Object.keys(process.textAnnotations)) provenanceTargets.add(id);
  }

  const relationships: BpmnRelationship[] = [];
  for (const relationship of elements(definitions.get('relationships'))) {
    const id = idOf(relationship);
    const rawType = text(relationship.get('type'));
    const type: RelationshipType = rawType === 'sourceUnknown' ? 'sourceUnknown' : 'basedOn';
    // 未知のtypeでも表示は続ける。原文をrawTypeへ残し、丸めたことを警告で知らせる。
    if (rawType !== 'basedOn' && rawType !== 'sourceUnknown') {
      warnings.push({
        code: 'unknown_relationship_type',
        message: `relationship ${id} のtype="${rawType}" は未知のためbasedOnとして表示します`,
        ref: id,
      });
    }
    const sources = strings(relationship.get('source'));
    const targets = strings(relationship.get('target'));
    for (const source of sources) {
      if (provenanceSources.has(source)) continue;
      warnings.push({
        code: 'dangling_relationship_source',
        message: `relationship ${id} のsource ${source} に対応する要素がありません`,
        ref: id,
      });
    }
    for (const target of targets) {
      if (provenanceTargets.has(target)) continue;
      warnings.push({
        code: 'dangling_relationship_target',
        message: `relationship ${id} のtarget ${target} に対応するtextAnnotationがありません`,
        ref: id,
      });
    }
    relationships.push({
      id,
      type,
      rawType,
      direction: str(relationship.get('direction')),
      sources,
      targets,
    });
  }

  return {
    id: idOf(definitions),
    targetNamespace: text(definitions.get('targetNamespace')),
    sourceDigest: contentDigest(xml),
    hasDiagram: elements(definitions.get('diagrams')).length > 0,
    processes,
    relationships,
    warnings,
  };
}
